// lib/constants.ts
const isBrowserLocalhost =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

export const FRONTEND_BASE_URL =
  process.env.REACT_APP_FRONTEND_BASE_URL ||
  (process.env.NODE_ENV === "development" || isBrowserLocalhost
    ? "http://localhost:3000"
    : "https://subscribe.hariprasadk.com");

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === "development" || isBrowserLocalhost
    ? "http://localhost:5001/api/v1"
    : `${FRONTEND_BASE_URL}/api/v1`);
export const ROLES = {
  USER: "User",
  ADMIN: "Admin",
  SUPER_ADMIN: "SuperAdmin",
  SALES: "Sales",
  SUPPORT: "Support",
} as const;

export const LOCAL_STORAGE_KEYS = {
  AUTH_TOKEN: "authToken",
  USER_ID: "userId",
  USER_ROLE: "userRole",
  IS_KYC_SUBMITTED: "isKycSubmitted",
  REDIRECT_AFTER_LOGIN: "redirectAfterLogin",
  USER_PHONE: "userPhone",
  TELEGRAM_ID_LINKED: "telegramIdLinked",
  SLACK_ID_LINKED: "slackIdLinked",
  USER_LOGIN_ID: "userLoginId", // Added Login ID to storage keys
  USER_NAME: "userName", // Added User Name to storage keys
};

export const SUBSCRIPTION_STATUS = {
  ACTIVE: "active",
  EXPIRED: "expired",
  REVOKED: "revoked",
  PENDING: "pending",
  AGSIGN: "agsign",
  GETKYC: "kycSub",
};

// Platform types for multi-platform support
export const PLATFORMS = {
  TELEGRAM: "telegram",
  DISCORD: "discord",
  SLACK: "slack",
} as const;

export type Platform = typeof PLATFORMS[keyof typeof PLATFORMS];

// Platform display names and metadata
export const PLATFORM_CONFIG = {
  [PLATFORMS.TELEGRAM]: {
    name: "Telegram",
    icon: "faTelegram",
    color: "#0088cc",
    bgColor: "bg-[#0088cc]/20",
    textColor: "text-[#0088cc]",
    description: "Telegram channel or group",
  },
  [PLATFORMS.DISCORD]: {
    name: "Discord",
    icon: "faDiscord",
    color: "#5865F2",
    bgColor: "bg-[#5865F2]/20",
    textColor: "text-[#5865F2]",
    description: "Discord server with role-based access",
  },
  [PLATFORMS.SLACK]: {
    name: "Slack",
    icon: "faSlack",
    color: "#4A154B",
    bgColor: "bg-[#4A154B]/20",
    textColor: "text-[#4A154B]",
    description: "Slack channel with bot-managed membership",
  },
} as const;

// Feature flags (can be overridden by environment variables)
export const FEATURE_FLAGS = {
  DISCORD_SUPPORT_ENABLED: process.env.REACT_APP_DISCORD_ENABLED !== "false", // Default enabled
  SLACK_SUPPORT_ENABLED: process.env.REACT_APP_SLACK_ENABLED !== "false", // Default enabled
} as const;

export const ROUTES = {
  HOME: "/",
  LANDING: "/",
  LOGIN_USER: "/login",
  LOGIN_ADMIN: "/login/admin",
  REGISTER_ADMIN: "/register/admin",
  VERIFY_OTP: "/verify-otp",
  NAME_MAIL_LINK: (role: string) => `/nameMail?role=${role}`,

  USER_DASHBOARD: "/user/my-groups",
  USER_PROFILE: "/user/profile",

  ADMIN_DASHBOARD: "/admin/overview", // Assuming Admin dashboard is the channel list
  ADMIN_CHANNELS: "/admin/channels",
  ADMIN_LEADS: "/admin/leads",
  ADMIN_COMBOS: "/admin/combo",
  ADMIN_COMBO_EDIT: (id: string) => `/admin/combo/${id}/edit`,
  ADMIN_COMBO_SUBS: (id: string) => `/admin/combo/${id}/subscriptions`, // Admin view of combo subs
  ADMIN_COMBO_OVERVIEW: (id: string) => `/admin/combo/${id}/overview`, // Admin view of combo overview

  ADMIN_PROFILE: "/admin/profile", // Admin profile page
  ADMIN_MY_TEAM: "/admin/my-team", // Admin team view
  ADMIN_COUPONS: "/admin/coupons", // Admin universal coupon management
  ADMIN_SETTINGS: "/admin/settings", // Admin client settings module
  ADMIN_DISCORD: "/admin/discord", // Admin Discord management
  ADMIN_SLACK: "/admin/slack", // Admin Slack management
  ADMIN_CHANNEL_NEW: "/admin/channels/new",
  ADMIN_CHANNEL_EDIT: (id: string) => `/admin/channels/${id}/edit`,
  ADMIN_CHANNEL_SUBS: (id: string) => `/admin/channels/${id}/subscriptions`, // Admin view of channel subs
  ADMIN_CHANNEL_PLANS: (id: string) => `/admin/channels/${id}/plans`, // Admin view of channel plans
  ADMIN_CHANNEL_OVERVIEW: (id: string) => `/admin/channels/${id}/overview`, // Admin view of channel plans

  ADMIN_PLANS_NEW: "/admin/plans/new",
  ADMIN_PLAN_EDIT: (planId: string) => `/admin/plans/${planId}/edit`,

  SUPER_ADMIN_DASHBOARD: "/superadmin/overview", // SuperAdmin dashboard
  SUPER_ADMIN_USERS: "/superadmin/users", // SuperAdmin user management
  SUPER_ADMIN_TEAM: "/superadmin/teams", // SuperAdmin user management
  SUPER_ADMIN_CHANNELS: "/superadmin/channels", // SuperAdmin channel management
  SUPER_ADMIN_PLANS: "/superadmin/plans", // SuperAdmin plan management (repurposed)
  SUPER_ADMIN_SUBSCRIPTIONS: "/superadmin/subscriptions", // Added SuperAdmin subscription list
  SUPER_ADMIN_INTEGRATIONS: "/superadmin/integrations",
  SUPER_ADMIN_ROLES: "/superadmin/roles",
  SUPER_ADMIN_SETTINGS: "/superadmin/settings",
  SUPER_ADMIN_LOGS: "/superadmin/logs",
  SUPER_ADMIN_FORGOT: "/superadmin/forgot",

  PUBLIC_CHANNEL_PLANS: (referralCode: string) => `/channel/${referralCode}`,
  PUBLIC_COMBO_PLANS: (referralCode: string) => `/combo/${referralCode}`,

  TERMS: "/terms",
  PRIVACY: "/privacy",
  SUPPORT: "/support",
};

// Additional Admin Utility Routes
export const ADMIN_UTILS = {
  MANUAL_ADD_USER: "/admin/manual-add",
} as const;
